import React, { createContext, useState, useEffect, useContext } from 'react';
// Fallback to local JSON for development when CONFIG_URL is not set
import fallbackData from '../data/businessInfo.json';

// Config URL passed via environment variable at build time
const CONFIG_URL = process.env.REACT_APP_CONFIG_URL;
// Whether to use local fallback on fetch error (default: true)
const USE_LOCAL_FALLBACK = process.env.REACT_APP_USE_LOCAL_FALLBACK !== 'false';

// Create the context
const BusinessContext = createContext();

// Custom hook to use the business context
export const useBusinessData = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusinessData must be used within a BusinessProvider');
  }
  return context;
};

// Provider component
export const BusinessProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        // If CONFIG_URL is set, fetch from gist; otherwise use bundled fallback
        if (CONFIG_URL) {
          // Add cache-busting timestamp to bypass CDN cache
          // TODO: Remove this once client setup is complete
          const cacheBuster = `?t=${Date.now()}`;
          const urlWithCacheBuster = CONFIG_URL + cacheBuster;
          console.log('Fetching config from:', urlWithCacheBuster);
          const response = await fetch(urlWithCacheBuster);
          if (!response.ok) {
            throw new Error(`Failed to fetch config: ${response.status}`);
          }
          const configData = await response.json();
          setData(configData.business);
        } else {
          // Development fallback: use local JSON file
          console.log('Using local fallback config (CONFIG_URL not set)');
          setData(fallbackData.business);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading business data:', err);
        
        if (USE_LOCAL_FALLBACK) {
          // Fallback to bundled data on fetch error
          console.log('Falling back to bundled config due to error');
          setData(fallbackData.business);
          setLoading(false);
        } else {
          // Don't use fallback, show error
          setError('Failed to load business data. Please try again later.');
          setLoading(false);
        }
      }
    };
    loadBusinessData();
  }, []);

  // Function to check if a specific data path exists
  const hasData = (path) => {
    if (!data) return false;
    
    const parts = path.split('.');
    let current = data;
    
    for (const part of parts) {
      if (current[part] === undefined || current[part] === null) {
        return false;
      }
      current = current[part];
    }
    
    return true;
  };

  // Function to get data safely (with optional default value)
  const getData = (path, defaultValue = null) => {
    if (!data) return defaultValue;
    
    const parts = path.split('.');
    let current = data;
    
    for (const part of parts) {
      if (current[part] === undefined || current[part] === null) {
        return defaultValue;
      }
      current = current[part];
    }
    
    return current;
  };
  
  // Utility function to get an image URL (handles external URLs directly)
  const getImageUrl = (path, defaultValue = null) => {
    const imageUrl = getData(path, defaultValue);
    // Return the URL as is, assuming all URLs are external
    return imageUrl;
  };

  // Value to be provided by the context
  const value = {
    data,
    loading,
    error,
    hasData,
    getData,
    getImageUrl
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext; 